import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { styled, applyClassName } from "@glitz/react";
import { GrCart } from "react-icons/gr";
import { Product } from "../types";
import { useCart } from "../context/CartContext";
import ImageGallery from "../components/ImageGallery";
import ProductTitleSlim from "../components/ProductTitleSlim";
import Price from "../components/Price";
import { spacing, grey, green, white } from "../theme";

type ProductCardProps = {
  product: Product;
};

type PriceProps = {
  onSale: boolean;
  salePrice: string;
  regularPrice: string;
  price: string;
};

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const [productPrice, setProductPrice] = useState<PriceProps>({
    onSale: false,
    salePrice: "",
    regularPrice: "",
    price: "",
  });
  const { addToCart } = useCart();

  useEffect(() => {
    setProductPrice({
      onSale: product.on_sale,
      salePrice: product.sale_price,
      regularPrice: product.regular_price,
      price: product.price,
    });
  }, []);
  const navigate = useNavigate();
  const handleOpenProduct = () => {
    navigate(`/product/${product.slug || product.id}`);
  };

  const isSimple = product.type === "simple";

  const inStock = product.in_stock ?? (product.stock_status === "instock");
  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    addToCart({
      id: product.id,
      sku: product.sku || undefined,
      name: product.name,
      price: parseFloat(product.price),
      quantity: 1,
      img: product.images?.[0]?.src,
      in_stock: inStock,
      stock_status: product.stock_status,
      shipping_class: product.shipping_class,
    });
  };

  return (
    <ProductCardWrapper
      key={product.id}
      className="product-card-wrapper"
      onClick={(e) => {
        e.stopPropagation();
        handleOpenProduct();
      }}
    >
      <ImageGallery images={product.images} showThumbnail={false} />
      <ProductTitleSlim title={product.name} />
      <BottomRow className="bottom-row">
        <Price productPrice={productPrice} />
        {isSimple && (
          <CartButton onClick={handleAddToCart}>
            <CartIcon />
          </CartButton>
        )}
      </BottomRow>
    </ProductCardWrapper>
  );
};
const ProductCardWrapper = styled.div({
  width: "300px",
  maxWidth: "100%",
  cursor: "pointer",
  border: { xy: { style: "solid", color: grey, width: "2px" } },
  padding: { x: spacing.m, y: spacing.s },
  alignContent: "end",
});

const BottomRow = styled.div({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const CartButton = styled.button({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "36px",
  height: "36px",
  borderRadius: "50%",
  border: { xy: { style: "none", width: 0, color: "transparent" } },
  backgroundColor: green,
  color: white,
  cursor: "pointer",
  transitionProperty: "transform, opacity",
  transitionDuration: "0.15s",
  ":hover": {
    transform: "scale(1.1)",
    opacity: 0.9,
  },
});

const CartIcon = styled(applyClassName(GrCart), {
  fontSize: "16px",
});

export default ProductCard;
